import React, { useState, useEffect, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Divider,
  Button,
  Grid,
  CircularProgress,
} from '@material-ui/core';
import { Form } from '@unform/web';
import { differenceInMinutes } from 'date-fns';
import { useStyles } from './styles';
import { generateId } from './utils';
import {
  EducationMission,
  EducationRequest,
  EducationResponse,
  OrientationObject,
  AtividadesRealizadasObject,
  MateriaisUtilizadosObject,
  AbordagemDetailsObject,
  EquipeMissaoObject,
} from '../../@types/educationTypes';

import EducationTopForm from '../../components/EducationTopForm';
import AbordagensDetailFormGroup from '../../components/AbordagensDetailFormGroup';
import OrientationFormGroup from '../../components/OrientationFormGroup';
import MateriaisUtilizadosDetailFormGroup from '../../components/MateriaisUtilizadosDetailFormGroup';
import AtividadesRealizadasFormGroup from '../../components/AtividadesRealizadasFormGroup';
import EquipeMissaoFormGroup from '../../components/EquipeMissaoDetailFormGroup';

import ShowSnackbar, { SetSnackbarType } from '../../components/SnackBar';
import DialogWarningMessage from '../../components/DialogWarningMessage';
import { GoogleMapsReverseResponseType } from '../../@types/geocodingTypes';
import DialogConfirm from '../../components/DialogConfirm';
import DialogWarnActions, { DialogActionType } from '../../components/DialogWarnActions';
import { deleteEducationById, fetchEducationById, updateEducation } from '../../actions/educationActions';
import Colors from '../../components/Colors';

export interface Coordinates {
  lat: number;
  lng: number;
}

export interface PeriodType {
  occuredFrom: string;
  occuredTo: string;
}

interface ParamsType {
  pathId: string;
}

const initialEducationMission: EducationMission = {
  tipoDaMissao: 'AMC intinerante',
  focoDaMissao: 'Outros',
  descricaoDoFocoDaMissao: '',
  descricaoDoTipoDaMissao: '',
  endereco1: '',
  endereco2: '',
  numeroReferencia: '',
  bairro: '',
  _localId: generateId(),
};

const initialAddress: GoogleMapsReverseResponseType = {
  status: '',
  plus_code: {
    compound_code: '',
    global_code: '',
  },
  results: [
    {
      address_components: [
        {
          long_name: '',
          short_name: '',
          types: [' '],
        },
      ],
      formatted_address: '',
      geometry: {
        location: {
          lat: 0,
          lng: 0,
        },
        location_type: '',
      },
    },
  ],
};

// const initialAtividadesRealizadas: AtividadesRealizadas = {
//   tipo: 'Outros',
//   quantidade: 0,
//   _localId: generateId(),
// };

// const initialMateriaisUtilizados: MateriaisUtilizados = {
//   tipo: 'Outros',
//   especificacao: 'Não informado',
//   quantidade: 0,
//   _localId: generateId(),
// };

const initialPeriod: PeriodType = {
  occuredFrom: '',
  occuredTo: '',
};

interface UpdateLocation {
  address1: string;
  address2?: string;
  refPoint?: string;
  neighborhood?: string;
}

const initialPostObj: EducationRequest = {
  data: {
    educationMission: initialEducationMission,
    abordagemDetails: [],
    orientationDetails: [],
    atividadesRealizadasDetails: [],
    materiaisDetails: [],
    equipeMissaoDetails: [],
  },
  geom: '',
  schema: '',
  occurred_from: '',
  occurred_to: '',
  location_text: '',
};

const UpdateEducation: React.FC = () => {
  const classes = useStyles();
  const [period, setPeriod] = useState<PeriodType>(initialPeriod);
  const [latLng, setLatLng] = useState<Coordinates>({ lat: 0, lng: 0 });
  const [requiredFields, setRequiredFields] = useState<string[]>([]);
  const [updateLocation, setUpdateLocation] = useState<UpdateLocation>({
    address1: '',
    address2: '',
    neighborhood: '',
    refPoint: '',
  });
  const [adresses, setAdresses] = useState<{
    address1: string;
    address2: string;
    neighborhood: string;
  }>({
    address1: '',
    address2: '',
    neighborhood: '',
  });
  const [location, setLocation] = useState<GoogleMapsReverseResponseType>(initialAddress);
  const [orientationDetails, setOrientationDetails] = useState<OrientationObject[]>([]);
  const [intersection, setIntersection] = useState<'Sim' | 'Não' | 'Não informado'>(
    'Não informado',
  );
  const [atividadesRealizadasDetails, setAtividadesRealizadasDetails] = useState<
    AtividadesRealizadasObject[]
  >([]);
  const [reference, setReference] = useState<string>('');
  const [educationMissionDetails, setEducationMissionDetails] = useState<
    EducationMission
  >(initialEducationMission);
  // const [atividadesRealizadas, setAtividadesRealizadas] = useState<
  //   AtividadesRealizadas[]
  // >([]);
  const [materiaisUtilizadosDetails, setMateriaisUtilizadosDetails] = useState<
    MateriaisUtilizadosObject[]
  >([]);
  const [abordagemDetails, setAbordagemDetails] = useState<AbordagemDetailsObject[]>([]);
  const [equipeMissao, setEquipeMissao] = useState<EquipeMissaoObject[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [snackBar, setSnackbar] = useState<SetSnackbarType>({ open: false, message: '' });
  const [warnMessages, setWarnMessages] = useState<{ key: string; message: string }[]>(
    [],
  );
  const [openDialogWarn, setOpenDialogWarns] = useState<boolean>(false);
  const [openDialogConfirm, setDialogConfirm] = useState<boolean>(false);
  const [dialogAction, setDialogAction] = useState<DialogActionType>({
    open: false,
    title: '',
    actions: [],
  });

  const { pathId } = useParams<ParamsType>();

  const history = useHistory();
  const navigate = (path: string) => {
    history.push(path);
  };

  const mountStates = (data: EducationResponse) => {
    setLatLng({
      lat: data.geom.coordinates[1],
      lng: data.geom.coordinates[0],
    });
    setPeriod({
      occuredFrom: data.occurred_from,
      occuredTo: data.occurred_to,
    });

    setEducationMissionDetails(data.data.educationMission);
    setAtividadesRealizadasDetails(
      data.data.atividadesRealizadasDetails as AtividadesRealizadasObject[],
    );
    setMateriaisUtilizadosDetails(
      data.data.materiaisDetails as MateriaisUtilizadosObject[],
    );
    setAbordagemDetails(data.data.abordagemDetails as AbordagemDetailsObject[]);
    setEquipeMissao(data.data.equipeMissaoDetails as EquipeMissaoObject[]);
    setOrientationDetails(data.data.orientationDetails as OrientationObject[]);
  };

  const postRecord = async (record: EducationRequest) => {
    setLoading(true);
    try {
      const { data } = await updateEducation(pathId, record);
      mountStates(data);
      setWarnMessages([]);

      setDialogAction({
        open: true,
        title: 'Missão de Educação atualizada com sucesso!',
        actions: [
          {
            btnText: 'Registros',
            color: 'primary',
            action: () => navigate('/registros/educacao'),
          },
          {
            btnText: 'Mapa',
            color: 'primary',
            action: () => navigate('/mapa/educacao'),
          },
          {
            btnText: 'Exibir',
            color: 'primary',
            action: () => navigate(`/registro/educacao/${pathId}`),
          },
        ],
      });
    } catch (error) {
      setLoading(false);
      setSnackbar({
        open: true,
        message: 'Erro ao atualizar a Missão de Educação.',
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteRecords = async (id: string) => {
    setLoading(true);
    try {
      await deleteEducationById(id);

      setDialogAction({
        open: true,
        title: 'Missão de Educação removida com sucesso!',
        actions: [
          {
            btnText: 'Registros',
            color: 'primary',
            action: () => navigate('/registros/educacao'),
          },
          {
            btnText: 'Mapa',
            color: 'primary',
            action: () => navigate('/mapa/educacao'),
          },
          {
            btnText: 'Nova Intervenção',
            color: 'primary',
            action: () => navigate('/add/educ'),
          },
        ],
      });
    } catch (error) {
      setSnackbar({ open: true, message: 'Erro ao remover a missão de educação.' });
    } finally {
      setLoading(false);
    }
  };

  const validateFields = (fieldsData: EducationRequest) => {
    setRequiredFields([]);
    const { data } = fieldsData;
    const formError: { key: string; message: string }[] = [];

    const diffMin = differenceInMinutes(
      new Date(period.occuredTo),
      new Date(period.occuredFrom),
    );
    const diffHour = diffMin / 60;

    if (latLng.lat === 0 || latLng.lng === 0) {
      formError.push({
        key: 'missionLocation',
        message: 'Informe a localização da missão.',
      });
    }

    if (diffHour <= 0) {
      formError.push({ key: 'missionDate', message: 'A missão não pode durar 0 horas.' });
    } else if (diffHour > 23) {
      formError.push({
        key: 'missionDate',
        message: 'A missão não pode durar mais que 23 horas.',
      });
    }

    if (formError.length === 0) {
      postRecord(fieldsData);
      window.scrollTo(0, 0);
      return;
    }

    setWarnMessages(formError);
    setOpenDialogWarns(true);
  };

  const handleSubmit = async () => {
    const bodyForm: EducationRequest = initialPostObj;
    bodyForm.geom = `POINT(${latLng.lng} ${latLng.lat})`;
    bodyForm.occurred_from = period.occuredFrom;
    bodyForm.occurred_to = period.occuredTo;
    bodyForm.data.educationMission = educationMissionDetails;
    bodyForm.data.abordagemDetails = abordagemDetails;
    bodyForm.data.atividadesRealizadasDetails = atividadesRealizadasDetails;
    bodyForm.data.equipeMissaoDetails = equipeMissao;
    bodyForm.data.materiaisDetails = materiaisUtilizadosDetails;
    bodyForm.data.orientationDetails = orientationDetails;

    bodyForm.schema = `${process.env.REACT_APP_EDUCATION_SCHEMA_ID}`;

    console.log(bodyForm);

    validateFields(bodyForm);
  };

  useEffect(() => {
    setLoading(true);
    async function loadEducation(id: string) {
      try {
        const { data } = await fetchEducationById(id);
        mountStates(data);
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'Não foi possível encontrar a missão de fiscalização.',
        });
      } finally {
        setLoading(false);
      }
    }
    pathId
      ? loadEducation(pathId)
      : setSnackbar({
          open: true,
          message: 'Não foi possível encontrar a missão de fiscalização.',
        });
  }, [pathId]);

  return (
    <Container className={classes.root}>
      <Typography variant="h5">Edição dos Detalhes da Missão de Educação</Typography>
      <Divider />
      <Form className={classes.formGroup} autoComplete="off" onSubmit={handleSubmit}>
        <Grid container xs={12}>
          <Grid item xs={12} md={10}>
            <EducationTopForm
              educationMission={educationMissionDetails}
              setEducationMission={setEducationMissionDetails}
              period={period}
              setPeriod={setPeriod}
              latlng={latLng}
              setLatLng={setLatLng}
              formErrors={warnMessages}
              setFormErrors={setWarnMessages}
              adresses={adresses}
              setAdresses={setAdresses}
              setIntersection={setIntersection}
              intersection={intersection}
              setReference={setReference}
              reference={reference}
            />

            <Grid item xs={12}>
              <AbordagensDetailFormGroup
                abordagemDetails={abordagemDetails}
                setAbordagemDetails={setAbordagemDetails}
                requiredFields={requiredFields}
              />
            </Grid>

            <Grid item xs={12}>
              <OrientationFormGroup
                orientation={orientationDetails}
                setOrientation={setOrientationDetails}
                formErrors={warnMessages}
                setFormErrors={setWarnMessages}
              />
            </Grid>

            <Grid item xs={12}>
              <MateriaisUtilizadosDetailFormGroup
                materiaisDetails={materiaisUtilizadosDetails}
                setMateriaisDetails={setMateriaisUtilizadosDetails}
                requiredFields={requiredFields}
              />
            </Grid>

            <Grid item xs={12}>
              <AtividadesRealizadasFormGroup
                atividadesRealizadas={atividadesRealizadasDetails}
                setAtividadesRealizadas={setAtividadesRealizadasDetails}
                requiredFields={requiredFields}
              />
            </Grid>

            <Grid item xs={12}>
              <EquipeMissaoFormGroup
                equipeMissaoDetails={equipeMissao}
                setEquipeMissaoDetails={setEquipeMissao}
                requiredFields={requiredFields}
              />
            </Grid>
          </Grid>

          <Grid>
            <div className={classes.buttonsContainer}>
              <div className={classes.buttonWrapper}>
                <Button
                  className={classes.buttons}
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={loading}
                  style={{ backgroundColor: Colors.main }}
                >
                  Atualizar Missão
                </Button>
                {loading && (
                  <CircularProgress size={24} className={classes.buttonProgress} />
                )}
              </div>
              <div className={classes.buttonWrapper}>
                <Button
                  className={classes.buttons}
                  color="default"
                  onClick={() => setDialogConfirm(true)}
                  disabled={loading}
                  style={{ backgroundColor: '#C59054', color: 'white' }}
                >
                  Excluir Missão
                </Button>
                {loading && (
                  <CircularProgress
                    size={24}
                    color="secondary"
                    className={classes.buttonProgress}
                  />
                )}
              </div>
              <div className={classes.buttonWrapper}>
                <Button
                  className={classes.buttons}
                  variant="outlined"
                  color="secondary"
                  onClick={() => navigate('/registros/educacao')}
                  disabled={loading}
                  style={{ backgroundColor: 'white' }}
                >
                  Cancelar
                </Button>
                {loading && (
                  <CircularProgress
                    size={24}
                    color="secondary"
                    className={classes.buttonProgress}
                  />
                )}
              </div>
            </div>
          </Grid>
        </Grid>
      </Form>
      <ShowSnackbar
        open={snackBar.open}
        message={snackBar.message}
        setSnackbar={setSnackbar}
      />
      <DialogWarningMessage
        open={openDialogWarn}
        title="Algumas informações precisam de atenção."
        messages={warnMessages}
        setOpen={setOpenDialogWarns}
      />
      <DialogConfirm
        open={openDialogConfirm}
        title="Apagar Missão de Educação?"
        message="Voce está prestes a apagar essa missão de educação. Essa operação não pode ser desfeita. Você deseja confirmar essa ação?"
        setOpen={setDialogConfirm}
        onConfirm={() => deleteRecords(pathId)}
      />
      <DialogWarnActions dialogActions={dialogAction} setDialog={setDialogAction} />
    </Container>
  );
};

export default UpdateEducation;
